import { TCreateCaseScheduleRequestDto } from "@/api/services/main/case-schedule/dtos/create-case-schedule.dto";
import { TGetCaseScheduleListResDto } from "@/api/services/main/case-schedule/dtos/get-case-schedule-list.dto";
import { TGetCaseScheduleDetailResDto } from "@/api/services/main/case-schedule/dtos/get-case-schedule-detail.dto";
import { clientApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useMemo } from "react";

export const createCaseSchedule = async (caseId: number | string, caseScheduleDataRequest: TCreateCaseScheduleRequestDto) => {
  const { data } = await clientApi.post(`/cases/${caseId}/case-schedules`, caseScheduleDataRequest);
  return data;
};

export const updateCaseSchedule = async (
  caseId: number | string,
  scheduleId: number | string,
  caseScheduleDataRequest: TCreateCaseScheduleRequestDto,
) => {
  const { data } = await clientApi.put(`/cases/${caseId}/case-schedules/${scheduleId}`, caseScheduleDataRequest);
  return data;
};

export const deleteCaseSchedule = async (caseId: number | string, scheduleId: number | string) => {
  const { data } = await clientApi.delete(`/cases/${caseId}/case-schedules/${scheduleId}`);
  return data;
};

export const fetchCaseScheduleListApi = async (caseId: number | string): Promise<TGetCaseScheduleListResDto> => {
  const { data } = await clientApi.get<TGetCaseScheduleListResDto>(`/cases/${caseId}/case-schedules`);
  return data;
};

export const useCaseScheduleList = (caseId?: number | string) => {
  const isValidId = useMemo(() => {
    if (!caseId) return false;
    const idNumber = Number(caseId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [caseId]);

  return useQuery({
    queryKey: [QUERY_KEYS.CASE.FETCH_CASE_SCHEDULES, caseId],
    queryFn: async () => {
      return caseId ? await fetchCaseScheduleListApi(caseId) : undefined;
    },
    enabled: isValidId,
  });
};

export const fetchCaseScheduleDetailApi = async (caseId: number | string, scheduleId: number | string): Promise<TGetCaseScheduleDetailResDto> => {
  const { data } = await clientApi.get<TGetCaseScheduleDetailResDto>(`/cases/${caseId}/case-schedules/${scheduleId}`);
  return data;
};

export const useCaseScheduleDetail = (caseId?: number | string, scheduleId?: number | string) => {
  const isValidIds = useMemo(() => {
    if (!caseId || !scheduleId) return false;
    const caseIdNumber = Number(caseId);
    const scheduleIdNumber = Number(scheduleId);
    return (
      !isNaN(caseIdNumber) &&
      Number.isInteger(caseIdNumber) &&
      caseIdNumber > 0 &&
      !isNaN(scheduleIdNumber) &&
      Number.isInteger(scheduleIdNumber) &&
      scheduleIdNumber > 0
    );
  }, [caseId, scheduleId]);

  return useQuery({
    queryKey: [QUERY_KEYS.CASE.FETCH_CASE_SCHEDULE_DETAIL, caseId, scheduleId],
    queryFn: async () => {
      return caseId && scheduleId ? await fetchCaseScheduleDetailApi(caseId, scheduleId) : undefined;
    },
    enabled: isValidIds,
  });
};
